import { useCallback, useEffect, useState } from 'react';

export const QUICK_ACCESS_MAX = 30;

const storageKey = (userId) =>
  `promethee.mediaLibrary.quickAccess.${userId || 'anonymous'}`;

// Le stockage peut être indisponible (navigation privée, stockage bloqué) :
// l'accès rapide reste alors utilisable pour la session, sans être conservé.
const readShortcuts = (key) => {
  try {
    const value = JSON.parse(window.localStorage.getItem(key));
    return Array.isArray(value)
      ? value.filter((path) => typeof path === 'string' && path)
      : [];
  } catch {
    return [];
  }
};

const writeShortcuts = (key, shortcuts) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(shortcuts));
  } catch {
    // Pas de stockage : les raccourcis ne survivront pas au rechargement.
  }
};

// Raccourcis de l'accès rapide, propres à chaque utilisateur et à ce navigateur.
// Un raccourci est le chemin relatif d'un dossier de la bibliothèque.
export const useQuickAccess = (userId) => {
  const key = storageKey(userId);
  // La liste est liée à sa clé : après un changement d'utilisateur, on ne peut
  // jamais appliquer ni enregistrer la liste du précédent sous la nouvelle clé.
  const [state, setState] = useState(() => ({
    key,
    shortcuts: readShortcuts(key),
    dirty: false,
  }));
  if (state.key !== key) {
    setState({ key, shortcuts: readShortcuts(key), dirty: false });
  }
  const shortcuts = state.key === key ? state.shortcuts : readShortcuts(key);

  // L'écriture se fait hors de la mise à jour d'état, qui doit rester pure.
  useEffect(() => {
    if (state.dirty) writeShortcuts(state.key, state.shortcuts);
  }, [state]);

  const update = useCallback(
    (compute) =>
      setState((prev) => ({
        key,
        shortcuts: compute(
          prev.key === key ? prev.shortcuts : readShortcuts(key)
        ),
        dirty: true,
      })),
    [key]
  );

  const pin = useCallback(
    (path) =>
      update((prev) =>
        prev.includes(path) || prev.length >= QUICK_ACCESS_MAX
          ? prev
          : [...prev, path]
      ),
    [update]
  );

  const unpin = useCallback(
    (path) => update((prev) => prev.filter((item) => item !== path)),
    [update]
  );

  return {
    shortcuts,
    isPinned: (path) => shortcuts.includes(path),
    isFull: shortcuts.length >= QUICK_ACCESS_MAX,
    pin,
    unpin,
  };
};
